<script lang="ts" setup generic="T extends GenericObject = GenericObject">
import { computed, ref, useSlots } from 'vue';
import {
  IconSortDescending,
  IconSortAscending,
  IconEyeOff,
  IconPinned,
  IconPinnedOff,
  IconFilterPlus,
  IconSparkles,
  IconCaretDownFilled,
  IconCaretUpFilled,
} from '@/icons';
import { ObActionList, ObActionListItem } from '../../action-list';
import { ObActionMenu } from '../../action-menu';
import { ObTooltip } from '../../tooltip';
import { useTableContext } from '../shared';
import type { GenericObject } from '../../../../shared/types';
import type { UiDataTableColumn } from '../interfaces';
interface Props {
  activeFilterCounts?: Record<string, number>;
  column?: UiDataTableColumn<T>;
  withEmbeddedColumnFilters?: boolean;
}
defineOptions({ inheritAttrs: false });
const props = defineProps<Props>();
const slots = useSlots();
const {
  pinnedColumnsStartKeys,
  sorting,
  hideColumn,
  startDragging,
  draggedColumnIndex,
  draggedColumnKey,
  targetColumnIndex,
  targetColumnKey,
  setSorting,
  pinColumn,
  unpinColumn,
  columnsPinControl,
  columnsVisibilityControl,
  columnsReordering,
  filterByColumnEnabled,
  filterableColumnKeys,
  filterByColumn,
  askAiByColumnEnabled,
  askAiByColumn,
  resolveI18n,
} = useTableContext<T>();
const pinned = computed(
  () => props.column && pinnedColumnsStartKeys.value.includes(props.column.key),
);
const canFilter = computed(() => {
  if (!props.column) {
    return false;
  }
  if (!filterByColumnEnabled.value) {
    return false;
  }
  return Boolean(props.column.filterKey || filterableColumnKeys.value.has(props.column.key));
});
const showEmbeddedColumnFilters = computed(
  () => Boolean(props.withEmbeddedColumnFilters && canFilter.value && slots.menu),
);
const resolvedFilterKey = computed(() => {
  if (!props.column) {
    return null;
  }
  if (props.column.filterKey) {
    return props.column.filterKey;
  }
  if (filterableColumnKeys.value.has(props.column.key)) {
    return props.column.key;
  }
  return null;
});
const activeFilterCount = computed(() => {
  const filterKey = resolvedFilterKey.value;
  if (!filterKey) {
    return 0;
  }
  return props.activeFilterCounts?.[filterKey] ?? 0;
});
function filterColumn() {
  if (!props.column) {
    return;
  }
  filterByColumn(props.column.key, props.column.filterKey);
}
function askAi() {
  if (!props.column) {
    return;
  }
  askAiByColumn(props.column);
}
function pin() {
  if (!props.column) {
    return;
  }
  pinColumn(props.column.key);
}
function unpin() {
  if (!props.column) {
    return;
  }
  unpinColumn(props.column.key);
}
function setAscendingSort() {
  if (!props.column) {
    return;
  }
  setSorting({
    sortBy: props.column.key,
    sortOrder: 'asc',
  });
}
function setDescendingSort() {
  if (!props.column) {
    return;
  }
  setSorting({
    sortBy: props.column.key,
    sortOrder: 'desc',
  });
}
let shouldResetSorting = false;
function toggleSorting() {
  if (!props.column) {
    return;
  }
  const { sortBy, sortOrder } = sorting.value;
  if (shouldResetSorting) {
    setSorting({
      sortBy: null,
      sortOrder: 'asc',
    });
    shouldResetSorting = false;
    return;
  }
  if (sortBy !== props.column.key) {
    setDescendingSort();
    return;
  }
  shouldResetSorting = true;
  if (sortOrder === 'asc') {
    setDescendingSort();
    return;
  }
  setAscendingSort();
}
const sortingActive = computed(() => props.column && sorting.value.sortBy === props.column.key);
const dropdownOpen = ref(false);
function onMouseDown(event: MouseEvent) {
  if (
    !columnsReordering.value ||
    !props.column ||
    props.column.frozenStart ||
    props.column.frozenEnd
  ) {
    return;
  }
  startDragging(event, props.column.key);
}
const hasMenu = computed(() => {
  return (
    !props.column?.frozenStart &&
    !props.column?.frozenEnd &&
    (props.column?.sortable ||
      columnsPinControl.value ||
      columnsVisibilityControl.value ||
      canFilter.value ||
      askAiByColumnEnabled.value)
  );
});
const hasSecondaryMenuActions = computed(() => {
  return Boolean(
    props.column?.sortable ||
      columnsPinControl.value ||
      columnsVisibilityControl.value ||
      (canFilter.value && !showEmbeddedColumnFilters.value) ||
      askAiByColumnEnabled.value,
  );
});
</script>

<template>
  <ObTooltip
    placement="top"
    :activate-delay="200"
    :content="props.column?.tooltip"
    :disabled="!props.column?.tooltip"
  >
    <template #host="{ hostProps, hostRef }">
      <div
        v-bind="{ ...$attrs, ...hostProps }"
        :ref="hostRef"
        :class="[
          $style.root,
          {
            [$style.alignEnd]: props.column?.align === 'end',
            [$style.alignCenter]: props.column?.align === 'center',
            [$style.contentWrap]: props.column?.headingWrap,
            [$style.sortingActive]: sortingActive,
            [$style.dropdownOpen]: dropdownOpen,
          },
        ]"
        @mousedown.left="onMouseDown"
      >
        <div :class="$style.content">
          <span v-if="activeFilterCount > 0" :class="$style.filterCount">{{ activeFilterCount }}</span>
          <span :class="[$style.label, { [$style.labelFiltered]: activeFilterCount > 0 }]">
            <slot />
          </span>
        </div>
        <ObActionMenu v-if="hasMenu && props.column" v-model:open="dropdownOpen" overlay>
          <template #host="{ hostProps: menuHostProps }">
            <button v-bind="menuHostProps" type="button" :class="$style.button" />
          </template>
          <template v-if="showEmbeddedColumnFilters">
            <slot name="menu" />
            <div v-if="hasSecondaryMenuActions" :class="$style.menuDivider" />
          </template>
          <ObActionList v-if="hasSecondaryMenuActions">
            <template v-if="props.column.sortable">
              <ObActionListItem @select="setAscendingSort()">
                <template #leadingVisual> <IconSortAscending /> </template>
                {{ resolveI18n('sortAscending') }}
              </ObActionListItem>
              <ObActionListItem @select="setDescendingSort()">
                <template #leadingVisual> <IconSortDescending /> </template>
                {{ resolveI18n('sortDescending') }}
              </ObActionListItem>
            </template>
            <template v-if="columnsPinControl">
              <ObActionListItem v-if="pinned" @select="unpin()">
                <template #leadingVisual> <IconPinnedOff /> </template>
                {{ resolveI18n('unpinColumn') }}
              </ObActionListItem>
              <ObActionListItem v-else @select="pin()">
                <template #leadingVisual> <IconPinned /> </template>
                {{ resolveI18n('pinColumn') }}
              </ObActionListItem>
            </template>
            <ObActionListItem
              v-if="columnsVisibilityControl"
              @select="hideColumn(props.column.key)"
            >
              <template #leadingVisual> <IconEyeOff /> </template>
              {{ resolveI18n('hideColumn') }}
            </ObActionListItem>
            <ObActionListItem
              v-if="canFilter && !showEmbeddedColumnFilters"
              @select="filterColumn()"
            >
              <template #leadingVisual> <IconFilterPlus /> </template>
              {{ resolveI18n('filterByColumn') }}
            </ObActionListItem>
            <ObActionListItem
              v-if="askAiByColumnEnabled"
              :class="$style.askAiAction"
              @select="askAi()"
            >
              <template #leadingVisual> <IconSparkles /> </template>
              {{ resolveI18n('askAi') }}
            </ObActionListItem>
          </ObActionList>
        </ObActionMenu>
        <div v-if="props.column?.sortable" :class="$style.controls">
          <button type="button" :class="$style.sortingButton" @click="toggleSorting()">
            <IconCaretDownFilled v-if="!sortingActive || sorting.sortOrder === 'desc'" size="16" />
            <IconCaretUpFilled v-else size="16" />
          </button>
        </div>
      </div>
      <div
        v-if="
          props.column &&
          props.column.key === targetColumnKey &&
          props.column.key !== draggedColumnKey
        "
        :class="[
          $style.dropIndicator,
          {
            [$style.dropIndicatorStart]: draggedColumnIndex > targetColumnIndex,
          },
        ]"
      />
    </template>
  </ObTooltip>
</template>

<style lang="scss" module>
@use '../../../styles/shared';
@use '../shared' as tableSharedStyles;

.root {
  position: relative;
  display: flex;
  box-sizing: border-box;
  padding: 12px;
  font-size: 13px;
  line-height: 16px;
  width: 100%;
  align-items: center;
  background: #fff;
  font-weight: 500;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-right: 1px solid tableSharedStyles.$border-color;
    z-index: tableSharedStyles.$z-index-border;
    pointer-events: none;
  }

  &:hover,
  &.dropdownOpen {
    background: var(--ui2-state-hover-lvl-1, rgba(0, 0, 0, 0.04));
  }
}

.content {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: inherit;
  color: var(--ui2-content-primary, #010000);
}

.labelFiltered {
  color: var(--ui2-background-accent-primary, #226b6c);
}

.filterCount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  flex: none;
  background: var(--ui2-background-accent-primary, #226b6c);
  color: var(--ui2-static-white, #fff);
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
}

.contentWrap {
  .content {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    align-items: flex-start;
  }

  .label {
    display: -webkit-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: normal;
    overflow-wrap: break-word;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}

.alignCenter {
  justify-content: center;

  .content {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: visible;
  }
}

.alignEnd {
  justify-content: flex-end;
}

.button {
  @include shared.reset-button();
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: tableSharedStyles.$z-index-clickable-cel;

  &:focus-visible {
    box-shadow: inset 0 0 0 2px #3355ff;
  }
}

.button:active,
.dropdownOpen .button {
  box-shadow: inset 0 0 0 2px #7e57c2;
}

.controls {
  display: flex;
  position: relative;
  pointer-events: none;
  z-index: tableSharedStyles.$z-index-clickable-cel + 1;
}

.sortingButton {
  @include shared.reset-button();
  pointer-events: auto;
  width: 16px;
  height: 16px;
  color: var(--ui2-content-primary, #010000);
  margin-left: 4px;
}

.root:not(.sortingActive) .sortingButton {
  color: #d2d3d9;
}

.dropIndicator {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background-color: #085cd9;
  pointer-events: none;
  z-index: tableSharedStyles.$z-index-drop-indicator;
}

.dropIndicatorStart {
  left: 0;
  right: auto;
}

.menuDivider {
  border-top: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
}

.askAiAction {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid transparent;
  background: var(--ui2-background-primary, #fff);
  color: var(--ui2-background-accent-primary, #226b6c);
  isolation: isolate;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300%;
    aspect-ratio: 1;
    transform-origin: center;
    translate: -50% -50%;
    border-radius: 50%;
    background: conic-gradient(
      from 0deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 0deg,
      var(--ui2-primitive-pine-forest-500, rgba(34, 107, 108, 1)) 45deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 90deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 360deg
    );
    animation: spinBorder 1.5s linear infinite;
    z-index: -2;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1.4px;
    border-radius: 6px;
    background: var(--ui2-background-primary, #fff);
    z-index: -1;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  &,
  > button {
    border-radius: 6px;
    color: var(--ui2-background-accent-primary, #226b6c);
    background: transparent;
  }

  &,
  > button svg,
  > button svg path {
    fill: currentColor !important;
  }

  &:hover:not(.disabled),
  &[data-is-active-descendant],
  > button:hover:not(:disabled),
  > button[data-is-active-descendant] {
    background:
      linear-gradient(0deg, rgba(34, 107, 108, 0.08) 0%, rgba(34, 107, 108, 0.08) 100%),
      var(--ui2-background-primary, #fff);
  }
}

@keyframes spinBorder {
  to {
    transform: rotate(360deg);
  }
}
</style>
